// src/pages/RoomListPage.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";

import {
  Table,
  Card,
  Button,
  Space,
  Typography,
  Tag,
  Badge,
  Popover,
  Image,
  Modal,
  notification,
  Breadcrumb,
  Alert,
  Tooltip,
  Popconfirm,
} from "antd";
import {
  InfoCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  QrcodeOutlined,
  EyeOutlined,
  ReloadOutlined,
} from "@ant-design/icons";

import "./RoomListPage.css";
import RoomDetailDrawer from "../components/RoomDetailDrawer";
import RoomEditModal from "../components/RoomEditModal";
import AddRoomModal from "../components/AddRoomModal";

const { Text, Title } = Typography;
const { confirm } = Modal;

// Slim / neat table styling
const slimTableStyles = `
  .slim-table .ant-table-thead > tr > th{
    padding: 6px 8px !important;
    font-size: 12px !important;
    font-weight: 600;
    background: #fafafa;
    white-space: nowrap;
  }

  .slim-table .ant-table-tbody > tr > td{
    padding: 6px 8px !important;
    font-size: 12px !important;
    vertical-align: top;
  }

  .slim-table .ant-tag{
    margin: 0 !important;
    font-size: 11px !important;
    padding: 0px 6px !important;
    border-radius: 10px !important;
    line-height: 18px;
  }

  .slim-table .ant-btn-sm{
    height: 24px !important;
    padding: 0 8px !important;
    font-size: 12px !important;
    border-radius: 6px !important;
  }

  .slim-table .ant-btn-icon-only.ant-btn-sm{
    width: 26px !important;
    padding: 0 !important;
  }

  .slim-table .ant-space{
    gap: 6px !important;
  }

  .slim-table .ant-table-pagination{
    margin: 12px 0 !important;
  }

  .room-header-card .ant-card-body{
    padding: 12px !important;
  }

  .room-header-row{
    display:flex;
    align-items:flex-start;
    justify-content:space-between;
    gap:12px;
  }

  .room-header-title{
    display:flex;
    flex-direction:column;
    gap:2px;
    min-width:240px;
  }

  .room-actions{
    display:flex;
    justify-content:flex-end;
    gap:8px;
    flex-wrap:wrap;
  }
`;

const RoomListPage = () => {
  const { authData } = useAuth();
  const hotelId = authData?.hotelId;

  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);

  const [setupLoading, setSetupLoading] = useState(true);
  const [setupError, setSetupError] = useState(null);
  const [setupStatus, setSetupStatus] = useState({
    hasRoomTypes: false,
    hasPrices: false,
  });

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [showInfoPopup, setShowInfoPopup] = useState(false);

  // VIEW / EDIT state
  const [viewDrawerOpen, setViewDrawerOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [currentRoom, setCurrentRoom] = useState(null);

  const [roomTypes, setRoomTypes] = useState([]);

  // Inject styles once
  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.innerHTML = slimTableStyles;
    document.head.appendChild(styleElement);
    return () => document.head.removeChild(styleElement);
  }, []);

  // Clear currentRoom ONLY when nothing is open
  useEffect(() => {
    if (!viewDrawerOpen && !editModalOpen) {
      setCurrentRoom(null);
    }
  }, [viewDrawerOpen, editModalOpen]);

  const breadcrumbItems = useMemo(
    () => [
      {
        title: (
          <Link
            to={
              authData?.role?.toLowerCase() === "systemadmin"
                ? "/admin/dashboard"
                : "/hotels/dashboard"
            }
          >
            Home
          </Link>
        ),
      },
      { title: "Room Management" },
    ],
    [authData?.role]
  );

  const fetchRoomTypes = useCallback(async () => {
    if (!hotelId) return;
    try {
      const res = await axiosInstance.get(`/RoomTypes/GetRoomTypes/${hotelId}`);
      const list = Array.isArray(res.data) ? res.data : [];
      setRoomTypes(list.filter((rt) => (rt?.roomTypeId ?? 0) > 0));
    } catch (error) {
      console.error("Error fetching room types:", error);
      setRoomTypes([]);
    }
  }, [hotelId]);

  const fetchPrices = useCallback(async () => {
    if (!hotelId) return [];
    try {
      const res = await axiosInstance.get(`/Price/hotel/${hotelId}`);
      return Array.isArray(res.data) ? res.data : [];
    } catch (error) {
      console.error("Error fetching prices:", error);
      return [];
    }
  }, [hotelId]);

  const checkPrerequisites = useCallback(async () => {
    if (!hotelId) return;

    setSetupLoading(true);
    setSetupError(null);

    try {
      const roomTypesRes = await axiosInstance.get(
        `/RoomTypes/GetRoomTypes/${hotelId}`
      );

      let pricesRes;
      try {
        pricesRes = await axiosInstance.get(`/Price/hotel/${hotelId}`);
      } catch (error) {
        if (error.response?.status === 404) pricesRes = { data: [] };
        else throw error;
      }

      const rt = Array.isArray(roomTypesRes.data) ? roomTypesRes.data : [];
      const pr = Array.isArray(pricesRes.data) ? pricesRes.data : [];

      setSetupStatus({
        hasRoomTypes: rt.length > 0 && rt.some((x) => (x?.roomTypeId ?? 0) > 0),
        hasPrices: pr.length > 0,
      });
    } catch (error) {
      console.error("Error checking prerequisites:", error);
      setSetupStatus({ hasRoomTypes: false, hasPrices: false });
      setSetupError(error?.message || "Failed to check prerequisites.");
    } finally {
      setSetupLoading(false);
    }
  }, [hotelId]);

  useEffect(() => {
    if (!hotelId) return;
    fetchRoomTypes();
    fetchRooms();
    checkPrerequisites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelId]);

  const getStatusString = (status) => {
    const statusMap = {
      Available: "Available",
      Occupied: "Occupied",
      Maintenance: "Maintenance",
      Reserved: "Reserved",
      UnderMaintenance: "UnderMaintenance",
      Cleaning: "Cleaning",
      Dirty: "Dirty",
      OutOfService: "OutOfService",
      Unknown: "Unknown",
      0: "Available",
      1: "Occupied",
      2: "Maintenance",
      3: "Reserved",
      4: "UnderMaintenance",
      5: "Cleaning",
      6: "Dirty",
      7: "OutOfService",
      9: "Unknown",
    };
    return statusMap[status] || "Unknown";
  };

  const normalizeQrToDataUrl = (rawQR) => {
    if (!rawQR) return null;
    const v = String(rawQR);
    return v.startsWith("data:image") ? v : `data:image/png;base64,${v}`;
  };

  const fetchRooms = useCallback(async () => {
    if (!hotelId) return;

    setLoading(true);
    try {
      const res = await axiosInstance.get(`/Rooms/all/${hotelId}`);
      const list = Array.isArray(res.data) ? res.data : [];

      const hotelPrices = await fetchPrices();

      const roomsWithPrices = list.map((room) => {
        const roomTypeId = room?.roomTypeId ?? room?.RoomTypeId;
        const roomPrice = hotelPrices.find((p) => p?.roomTypeId === roomTypeId);

        const resolvedTypeName =
          room?.roomTypeName ||
          room?.roomType?.typeName ||
          roomTypes.find((rt) => rt?.roomTypeId === roomTypeId)?.typeName ||
          "Unknown";

        const rawQR = room?.qrCodeBase64 || room?.QRCodeBase64 || null;

        return {
          ...room,
          status: getStatusString(room?.status),
          roomTypeName: resolvedTypeName,
          typeName: resolvedTypeName,
          qrCodeBase64: normalizeQrToDataUrl(rawQR),
          price: roomPrice?.basePrice || 0,
          currency: roomPrice?.currency || "BWP",
          vatPercentage: roomPrice?.vatPercentage || 0,
          additionalLevies: roomPrice?.additionalLevies || 0,
          discount: roomPrice?.discount || 0,
        };
      });

      setRooms(roomsWithPrices);
    } catch (error) {
      console.error("Error fetching rooms:", error);
      notification.error({
        title: "Error",
        description: "Failed to load rooms.",
      });
      setRooms([]);
    } finally {
      setLoading(false);
    }
  }, [hotelId, roomTypes, fetchPrices]);

  const renderQRCode = (qrCodeBase64) => {
    if (!qrCodeBase64) return <Tag color="orange">No QR</Tag>;
    return (
      <Popover
        content={<Image src={qrCodeBase64} width={160} />}
        trigger="hover"
      >
        <Button
          size="small"
          icon={<QrcodeOutlined />}
          onClick={(e) => e.stopPropagation()}
        />
      </Popover>
    );
  };

  const formatMoney = (ccy, amount) => {
    const n = Number(amount ?? 0);
    if (ccy && /^[A-Z]{3}$/.test(ccy)) {
      try {
        return new Intl.NumberFormat(undefined, {
          style: "currency",
          currency: ccy,
          maximumFractionDigits: 2,
        }).format(n);
      } catch {
        // ignore
      }
    }
    return `${ccy || ""} ${n.toFixed(2)}`;
  };

  const calcFinalPrice = (price, record) => {
    const base = Number(price ?? 0);
    const fees = Number(record?.additionalLevies ?? 0);
    const vatPct = Number(record?.vatPercentage ?? 0);
    const discountPct = Number(record?.discount ?? 0);

    const totalBeforeVat = base + fees;
    const vatAmount = (totalBeforeVat * vatPct) / 100;
    const final = totalBeforeVat + vatAmount;
    const discounted = discountPct > 0 ? final * (1 - discountPct / 100) : final;

    return { base, fees, vatPct, vatAmount, final, discountPct, discounted };
  };

  const handleDelete = useCallback(
    (roomId) => {
      confirm({
        title: "Delete Room",
        content: "Are you sure you want to delete this room?",
        okText: "Delete",
        okButtonProps: { danger: true },
        onOk: async () => {
          try {
            await axiosInstance.delete(`/Rooms/${roomId}`);
            notification.success({
              title: "Success",
              description: "Room deleted successfully",
            });
            fetchRooms();
          } catch {
            notification.error({
              title: "Error",
              description: "Failed to delete room",
            });
          }
        },
      });
    },
    [fetchRooms]
  );

  const handleBatchDelete = useCallback(() => {
    confirm({
      title: `Delete ${selectedRowKeys.length} room(s)?`,
      content: "Are you sure you want to delete these rooms?",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await Promise.all(
            selectedRowKeys.map((id) => axiosInstance.delete(`/Rooms/${id}`))
          );
          notification.success({
            title: "Success",
            description: `${selectedRowKeys.length} rooms deleted successfully`,
          });
          setSelectedRowKeys([]);
          fetchRooms();
        } catch {
          notification.error({
            title: "Error",
            description: "Failed to delete rooms",
          });
        }
      },
    });
  }, [selectedRowKeys, fetchRooms]);

  const handleViewRoom = useCallback((room) => {
    setCurrentRoom(room);
    setEditModalOpen(false);
    setViewDrawerOpen(true);
  }, []);

  const handleEditRoom = useCallback((room) => {
    setCurrentRoom(room);
    setViewDrawerOpen(false);
    setEditModalOpen(true);
  }, []);

  const handleModalEditSuccess = useCallback(() => {
    setEditModalOpen(false);
    fetchRooms();
    notification.success({
      title: "Success",
      description: "Room updated successfully",
    });
  }, [fetchRooms]);

  const hasSelected = selectedRowKeys.length > 0;

  const columns = useMemo(() => {
    const statusColors = {
      Available: "green",
      Occupied: "red",
      Maintenance: "orange",
      Reserved: "blue",
      UnderMaintenance: "volcano",
      Cleaning: "gold",
      Dirty: "brown",
      OutOfService: "magenta",
      Unknown: "default",
    };

    return [
      {
        title: "Room",
        dataIndex: "roomNumber",
        key: "roomNumber",
        width: 120,
        ellipsis: true,
        render: (text, record) => (
          <Space size={6}>
            <Tag color="blue">#{record?.roomId}</Tag>
            <span title={text}>{text || "N/A"}</span>
          </Space>
        ),
      },
      {
        title: "Type",
        key: "type",
        width: 180,
        ellipsis: true,
        render: (_, record) =>
          record?.roomTypeName ||
          record?.typeName ||
          record?.roomType?.typeName ||
          "Unknown",
      },
      {
        title: "Status",
        key: "status",
        width: 150,
        render: (_, record) => (
          <Tag color={statusColors[record?.status] || "default"}>
            {record?.status || "Unknown"}
          </Tag>
        ),
      },
      {
        title: "Price",
        dataIndex: "price",
        key: "price",
        width: 220,
        render: (price, record) => {
          if (Number(price ?? 0) <= 0) return <Tag color="red">Price not set</Tag>;

          const ccy = record?.currency || "BWP";
          const p = calcFinalPrice(price, record);

          return (
            <Popover
              title="Price Breakdown"
              content={
                <div style={{ minWidth: 220 }}>
                  <div>Base: {formatMoney(ccy, p.base)}</div>
                  <div>Fees: {formatMoney(ccy, p.fees)}</div>
                  <div>
                    VAT ({p.vatPct}%): {formatMoney(ccy, p.vatAmount)}
                  </div>
                  {p.discountPct > 0 && (
                    <div>
                      Discount: {p.discountPct}% (-
                      {formatMoney(ccy, p.final - p.discounted)})
                    </div>
                  )}
                  <div style={{ marginTop: 6 }}>
                    <b>Total: {formatMoney(ccy, p.discounted)}</b>
                  </div>
                </div>
              }
              trigger="hover"
            >
              <Tag
                color={record?.discount > 0 ? "green" : "blue"}
                onClick={(e) => e.stopPropagation()}
                style={{ cursor: "pointer" }}
              >
                {formatMoney(ccy, p.discounted)}
              </Tag>
            </Popover>
          );
        },
      },
      {
        title: "QR",
        key: "qrCode",
        width: 90,
        render: (_, record) => renderQRCode(record?.qrCodeBase64),
      },
      {
        title: "",
        key: "action",
        fixed: "right",
        width: 120,
        render: (_, record) => (
          <Space size={6} onClick={(e) => e.stopPropagation()}>
            <Tooltip title="View">
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={() => handleViewRoom(record)}
              />
            </Tooltip>

            <Tooltip title="Edit">
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEditRoom(record)}
              />
            </Tooltip>

            <Popconfirm
              title="Delete this room?"
              description="This action cannot be undone."
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={() => handleDelete(record?.roomId)}
            >
              <Tooltip title="Delete">
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          </Space>
        ),
      },
    ];
  }, [handleViewRoom, handleEditRoom, handleDelete]);

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys,
      onChange: (keys) => setSelectedRowKeys(keys),
    }),
    [selectedRowKeys]
  );

  const canAddRoom = !setupLoading && setupStatus.hasRoomTypes && setupStatus.hasPrices;

  return (
    <div className="room-list-page" style={{ padding: 16 }}>
      <Breadcrumb items={breadcrumbItems} style={{ marginBottom: 12 }} />

      {/* Card: bordered -> variant */}
      <Card className="room-header-card" variant="outlined">
        <div className="room-header-row">
          <div className="room-header-title">
            <Space size={8} align="center">
              <Title level={5} style={{ margin: 0 }}>
                Room Management
              </Title>
              <Badge count={rooms.length} showZero />
              <Tooltip title="Guide">
                <Button
                  size="small"
                  icon={<InfoCircleOutlined />}
                  onClick={() => setShowInfoPopup(true)}
                />
              </Tooltip>
            </Space>
            <Text type="secondary">
              Manage rooms, pricing visibility, and QR access.
            </Text>
          </div>

          <div className="room-actions">
            {hasSelected && (
              <Button
                size="small"
                danger
                onClick={handleBatchDelete}
                icon={<DeleteOutlined />}
              >
                Delete Selected ({selectedRowKeys.length})
              </Button>
            )}

            <Button
              size="small"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setAddModalOpen(true)}
              disabled={!canAddRoom}
              loading={setupLoading}
            >
              Add Room
            </Button>

            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => {
                fetchRooms();
                fetchRoomTypes();
                checkPrerequisites();
              }}
              loading={loading}
            >
              Refresh
            </Button>
          </div>
        </div>
      </Card>

      <div style={{ height: 12 }} />

      {!setupLoading && !setupStatus.hasRoomTypes && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message={
            <span>
              No room types configured or failed to load room types. Please{" "}
              <Link to="/hotels/roomtypespage">create room types</Link> first.
              {setupError && (
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">Error: {setupError}</Text>
                </div>
              )}
            </span>
          }
        />
      )}

      {setupStatus.hasRoomTypes && !setupStatus.hasPrices && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message={
            <span>
              Room types exist but no prices configured. Please{" "}
              <Link to="/hotels/roompricepage">set prices</Link>.
            </span>
          }
        />
      )}

      {/* Card: bordered/bodyStyle -> variant/styles.body */}
      <Card variant="outlined" styles={{ body: { padding: 12 } }}>
        <Table
          className="slim-table"
          size="small"
          rowKey="roomId"
          rowSelection={{ type: "checkbox", ...rowSelection }}
          columns={columns}
          dataSource={rooms}
          loading={loading}
          tableLayout="fixed"
          scroll={{ x: 1000 }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
            showTotal: (total) => `Total ${total} room${total === 1 ? "" : "s"}`,
          }}
          onRow={(record) => ({
            onClick: () => handleViewRoom(record),
          })}
          locale={{
            emptyText: (
              // Space: direction -> orientation
              <Space orientation="vertical">
                <Text>No rooms found</Text>
                <Button
                  size="small"
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setAddModalOpen(true)}
                  disabled={!canAddRoom}
                  loading={setupLoading}
                >
                  Add Room
                </Button>
              </Space>
            ),
          }}
        />
      </Card>

      {/* Add Room */}
      {addModalOpen && (
        <AddRoomModal
          open={addModalOpen}
          onCancel={() => setAddModalOpen(false)}
          onSuccess={() => {
            setAddModalOpen(false);
            fetchRooms();
          }}
          roomTypes={roomTypes}
        />
      )}

      {/* Drawer + Edit */}
      {currentRoom && (
        <>
          <RoomDetailDrawer
            open={viewDrawerOpen}
            room={currentRoom}
            onClose={() => setViewDrawerOpen(false)}
          />

          <RoomEditModal
            open={editModalOpen}
            room={currentRoom}
            onCancel={() => setEditModalOpen(false)}
            onSuccess={handleModalEditSuccess}
            roomTypes={roomTypes}
          />
        </>
      )}

      {/* Guide */}
      <Modal
        title="Room Management Guide"
        open={showInfoPopup}
        onCancel={() => setShowInfoPopup(false)}
        footer={[
          <Button
            key="close"
            size="small"
            onClick={() => setShowInfoPopup(false)}
          >
            Close
          </Button>,
        ]}
      >
        <p>This module allows you to manage all rooms in your hotel.</p>
        <h4>Setup Sequence:</h4>
        <ol>
          <li>Room Types - Define categories (e.g., Standard, Deluxe)</li>
          <li>Pricing - Set rates for each room type</li>
          <li>Rooms - Add individual rooms with numbers and types</li>
        </ol>
        <h4>Current Status:</h4>
        <ul>
          <li>Room Types: {setupStatus.hasRoomTypes ? "✓ Configured" : "✗ Missing"}</li>
          <li>Pricing: {setupStatus.hasPrices ? "✓ Configured" : "✗ Missing"}</li>
        </ul>
        <p>
          QR codes are automatically generated for each room and can be scanned
          for quick access.
        </p>
      </Modal>
    </div>
  );
};

export default RoomListPage;
